import type { CurrencyIdentifier } from "@opensea/app-kit"
import { graphql } from "@opensea/graphql"
import type { FragmentOf } from "gql.tada"
import { readFragment } from "gql.tada"
import { useQuery } from "urql"
import { useTranslations } from "use-intl"
import {
  ActionTimeline,
  ActionTimelineError,
  ActionTimelineFragment,
  ActionTimelineSkeleton,
} from "../../../../components/ActionTimeline"
import { CrossEcosystemSwapNotification } from "../../../../components/CrossEcosystemSwapNotification"
import type { WalletDestination } from "../../../../components/DestinationWalletSelect/types"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../../constants"
import { useAddressCompatibility } from "../../../../hooks/useAddressCompatibility"
import { useIsAbstractWalletAccount } from "../../../../hooks/useIsAbstractWalletAccount"
import { useAddress } from "../../../../polychain/hooks/useAddress"
import type { SchedulableResponse } from "../../../../polychain/hooks/useScheduler"
import type { TransactionIdentifier } from "../../../../stores/currentTransactionStore"
import type { Address, Item } from "../../../../types"
import { useActionErrors } from "../../../hooks/useErrors"
import { useSetBlockchainActionErrors } from "../../../hooks/useSetBlockchainActionErrors"
import { useSetBlockchainActionErrorsFragment } from "../../../hooks/useSetBlockchainActionErrors.fragment"
import { MintActionTimelineDropFragment } from "./MintActionTimelineDrop.fragment"

export const MintActionTimelineFragment = graphql(`
  fragment MintActionTimeline on Collection {
    slug
    address
    chain {
      identifier
    }
  }
`)

const MintActionTimelineQuery = graphql(
  `
    query MintActionTimelineQuery(
      $address: Address!
      $fromAssets: [AssetQuantityInput!]!
      $toAssets: [AssetQuantityInput!]!
      $recipient: Address
    ) {
      swap(
        address: $address
        fromAssets: $fromAssets
        toAssets: $toAssets
        recipient: $recipient
        action: MINT
      ) {
        actions {
          ...ActionTimeline
        }
        errors {
          ...useSetBlockchainActionErrors
        }
      }
    }
  `,
  [ActionTimelineFragment, useSetBlockchainActionErrorsFragment],
)

type MintCompleteResponse = {
  transactionIdentifier: TransactionIdentifier
  isCrossChain: boolean
  toAsset: {
    contractAddress: string
    chain: string
    tokenId: string
  }
}

type Props = {
  collection: FragmentOf<typeof MintActionTimelineFragment>
  drop: FragmentOf<typeof MintActionTimelineDropFragment>
  tokenInputs: Item[]
  selectedCurrency: CurrencyIdentifier | undefined
  selectedCurrencyLoading: boolean
  onComplete: (
    response: MintCompleteResponse,
    receiptTransactions: TransactionIdentifier[],
  ) => void
  walletDestination?: WalletDestination
}

export function MintActionTimeline({
  collection: collectionKey,
  drop: dropKey,
  tokenInputs,
  selectedCurrency,
  selectedCurrencyLoading,
  onComplete,
  walletDestination,
}: Props) {
  const t = useTranslations("wallet.MintActionTimeline")
  const INSUFFICIENT_FUNDS_ERROR = "Insufficient funds"
  const collection = readFragment(MintActionTimelineFragment, collectionKey)
  const _drop = readFragment(MintActionTimelineDropFragment, dropKey)
  const address = useAddress()
  const isAbstractWallet = useIsAbstractWalletAccount()
  const isCrossChain = Boolean(
    selectedCurrency &&
      selectedCurrency.chainIdentifier !== collection.chain.identifier,
  )

  const isAddressCompatible = useAddressCompatibility(
    walletDestination?.address ?? address,
    collection.chain.identifier,
  )

  const toAssets = tokenInputs.map(item => ({
    asset: {
      contractAddress: item.contractAddress,
      chain: item.chain.identifier,
      tokenId: item.tokenId,
    },
    quantity: item.quantity.toString(),
  }))

  const fromAssets = selectedCurrency
    ? [
        {
          asset: {
            contractAddress: selectedCurrency.contractAddress,
            chain: selectedCurrency.chainIdentifier,
          },
        },
      ]
    : [
        {
          asset: {
            contractAddress: "0x0000000000000000000000000000000000000000",
            chain: collection.chain.identifier,
          },
        },
      ]

  const [{ data, error, fetching }, refetch] = useQuery({
    query: MintActionTimelineQuery,
    variables: {
      address: address as NonNullable<Address>,
      fromAssets,
      toAssets,
      recipient: walletDestination?.address,
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
    pause:
      (walletDestination && !isAddressCompatible) || selectedCurrencyLoading,
  })

  useSetBlockchainActionErrors(data?.swap.errors)
  const actionError = useActionErrors()

  const handleComplete = async (
    response: SchedulableResponse,
    receiptTransactions: TransactionIdentifier[],
  ) => {
    if (response.action === "TRANSACTION") {
      const transactionResponse: MintCompleteResponse = {
        transactionIdentifier: response.transactionIdentifier,
        isCrossChain,
        toAsset: {
          contractAddress: collection.address as string,
          chain: collection.chain.identifier,
          tokenId: "0",
        },
      }

      onComplete(transactionResponse, receiptTransactions)
    }
  }

  // TODO: Clean up ActionTimeline error handling
  if (fetching || selectedCurrencyLoading) {
    return <ActionTimelineSkeleton count={1} />
  }

  if (!isAddressCompatible) {
    return <CrossEcosystemSwapNotification />
  }

  // Check if Abstract wallet is trying to mint on a non-Abstract chain
  if (isAbstractWallet && isCrossChain) {
    return (
      <ActionTimelineError
        message={t("abstractWalletMintNotAllowed")}
        onRetry={refetch}
      />
    )
  }

  if (error) {
    console.error(error)
    return <ActionTimelineError message={t("error")} onRetry={refetch} />
  }

  return (
    <ActionTimeline
      actions={data?.swap.actions ?? []}
      insufficientFundsOnChain={
        actionError?.includes(INSUFFICIENT_FUNDS_ERROR)
          ? collection.chain.identifier
          : undefined
      }
      onAllActionsCompleted={handleComplete}
      onReload={refetch}
    />
  )
}
